import { useEffect, useState } from "react";

import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useSaveInfraSettings } from "@/features/servers/hooks/useInfraSettings";
import {
  DEFAULT_PLATFORM_NOTIFICATION,
  type IPlatformNotification,
} from "@/features/servers/schemas/servers";

interface PlatformNotificationEditorProps {
  notification: IPlatformNotification;
}

/**
 * Canales globales de notificación (viewmed_config.notification).
 */
export function PlatformNotificationEditor({
  notification,
}: PlatformNotificationEditorProps) {
  const saveSettings = useSaveInfraSettings();
  const [draft, setDraft] = useState<IPlatformNotification>(
    notification ?? DEFAULT_PLATFORM_NOTIFICATION
  );

  useEffect(() => {
    setDraft(notification ?? DEFAULT_PLATFORM_NOTIFICATION);
  }, [notification]);

  const handleSave = async () => {
    try {
      await saveSettings.mutateAsync({ notification: draft });
      toast.success("Notificaciones de plataforma guardadas");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "No se pudieron guardar"
      );
    }
  };

  const rows: { key: keyof IPlatformNotification; label: string }[] = [
    { key: "email", label: "Email" },
    { key: "push", label: "Push" },
    { key: "ws", label: "WhatsApp" },
  ];

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium">
          Notificaciones de plataforma
        </CardTitle>
        <CardDescription>
          Canales activos a nivel global (campo{" "}
          <code className="text-xs">notification</code> en viewmed_config).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="space-y-2">
          {rows.map(({ key, label }) => (
            <div
              key={key}
              className="flex items-center justify-between rounded-md border px-3 py-2"
            >
              <Label htmlFor={`notif-${key}`} className="cursor-pointer">
                {label}
              </Label>
              <Switch
                id={`notif-${key}`}
                checked={draft[key]}
                onCheckedChange={(checked) =>
                  setDraft((prev) => ({ ...prev, [key]: checked }))
                }
              />
            </div>
          ))}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handleSave()}
          disabled={saveSettings.isPending}
        >
          {saveSettings.isPending ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1.5" />
          )}
          Guardar notificaciones
        </Button>
      </CardContent>
    </Card>
  );
}
